"use client";

import { useCallback, useEffect, useState } from "react";
import { QUIZ_VARIANT_TEST_A, QUIZ_VARIANT_TEST_B } from "@/lib/utils/common/constants";

const SESSION_KEY = "quiz-card-dismissed";


export function useQuizScroll(variant?: string | null) {
  const [quizCardDismissed, setCardDismissed] = useState(false);
  const [chipVisible, setChipVisible] = useState(false);
  const [cardVisible, setCardVisible] = useState(false);

  const dismissQuizCard = useCallback(() => {
    sessionStorage.setItem(SESSION_KEY, "true");
    setCardDismissed(true);
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem(SESSION_KEY) === "true") {
      setCardDismissed(true);
    }
  }, []);

  // ── Variant A: scroll-depth thresholds ──
  useEffect(() => {
    if (variant !== QUIZ_VARIANT_TEST_A) return;

    const heroEl = document.getElementById("hero-banner");

    const check = () => {
      const scrollY = window.scrollY;
      const totalScrollable =
        document.documentElement.scrollHeight - window.innerHeight;
      const scrollDepth = totalScrollable > 0 ? scrollY / totalScrollable : 0;
      const heroBottom = heroEl
        ? heroEl.offsetTop + heroEl.offsetHeight
        : window.innerHeight;

      if (scrollY < heroBottom) {
        setChipVisible(false);
        setCardVisible(false);
      } else if (scrollDepth >= 0.7) {
        setChipVisible(true);
        setCardVisible(false);
      } else if (scrollDepth >= 0.4) {
        setChipVisible(quizCardDismissed);
        setCardVisible(!quizCardDismissed);
      } else {
        setChipVisible(true);
        setCardVisible(false);
      }
    };

    window.addEventListener("scroll", check, { passive: true });
    check();
    return () => window.removeEventListener("scroll", check);
  }, [variant, quizCardDismissed]);

  // ── Variant B: IntersectionObserver on hero-banner + quiz-inline-section
  useEffect(() => {
    if (variant !== QUIZ_VARIANT_TEST_B) return;

    const heroEl = document.getElementById("hero-banner");
    const inlineSectionEl = document.getElementById("quiz-inline-section");

    let heroVisible = true;
    let inlineVisible = false;

    const update = () => {
      setChipVisible(!heroVisible && !inlineVisible);
      setCardVisible(false);
    };

    const heroObserver = new IntersectionObserver(
      (entries) => {
        heroVisible = (entries[0]?.intersectionRatio ?? 0) >= 0.1;
        update();
      },
      { threshold: [0, 0.1] },
    );

    const inlineObserver = new IntersectionObserver(
      (entries) => {
        inlineVisible = (entries[0]?.intersectionRatio ?? 0) >= 0.5;
        update();
      },
      { threshold: [0, 0.50] },
    );

    if (heroEl) heroObserver.observe(heroEl);
    if (inlineSectionEl) inlineObserver.observe(inlineSectionEl);

    return () => {
      heroObserver.disconnect();
      inlineObserver.disconnect();
    };
  }, [variant]);

  return {
    quizCardDismissed,
    dismissQuizCard,
    quizChipVisible: chipVisible,
    quizCardVisible: cardVisible,
  };
}
